import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Bars4Icon,
  ExclamationTriangleIcon,
  EyeIcon,
  NoSymbolIcon,
  PaperClipIcon,
  UserGroupIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import {
  Rule,
  SectionSlot,
  SectionWithSlots,
  SpecialEventWithoutDates,
  UserPreference,
  UserPreferenceType,
  UserRequirement,
  UserRequirementType,
} from '@youshift/shared/types';
import { dateToString, minutesToHours } from '@youshift/shared/utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ScheduleGridRulesForUser } from '../../../../components/ScheduleGrids/ScheduleGridRulesForUser';
import Wrapper from '../../../../components/Wrapper';
import { useChainContext } from '../../../../layouts/ChainLayout';
import { useItrContext } from '../../../../layouts/IterationRootLayout/IterationRootLayout';
import { isExclusionRuleRequirement } from './helpers';
import { RuleTabType } from './types';
import { VirgueriaVersion } from '../../../../components/ManualAssignment/helpers';
import Virgueria from '../../../../components/ManualAssignment/Virgueria';

function formatValue(
  type: UserRequirementType,
  value: number,
  t: (key: string) => string,
  minutesToHours: (min: number) => number,
) {
  switch (type) {
    case UserRequirementType.DURATION:
      return `${value}h`;
    case UserRequirementType.COUNTER_UNIT:
      return `${value} ${t('manager.rulesConfig.counterUnitShort')}`;
    case UserRequirementType.SLOTS:
    default:
      return value;
  }
}

export default function RulesForUser() {
  const {
    allUserReqRules,
    iteration,
    sectionsWithSlots,
    users,
    epa,
    sectionSlotsDict,
  } = useItrContext();
  const { slotLabels } = useChainContext();

  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const sortedUsersList = Object.values(users).sort((a, b) =>
    `${a.firstname} ${a.lastname}`.localeCompare(
      `${b.firstname} ${b.lastname}`,
    ),
  );

  const userId = searchParams.get('user');
  const tab = searchParams.get('tab');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(() =>
    userId ? Number(userId) : sortedUsersList[0].id,
  );

  useEffect(() => {
    const userId = searchParams.get('user');
    if (userId) {
      setSelectedUserId(Number(userId));
    }
  }, [searchParams]);

  const navigate = useNavigate();

  const changeSelectedPerson = (direction: 'next' | 'previous') => {
    const currentIndex = sortedUsersList.findIndex(
      person => person.id === Number(selectedUserId),
    );
    const totalUsers = sortedUsersList.length;
    const offset = direction === 'next' ? 1 : -1;
    const newIndex = (currentIndex + offset + totalUsers) % totalUsers;
    setSelectedUserId(sortedUsersList[newIndex].id);
  };

  const handleUserChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedUserId(Number(e.target.value));
  };

  const { rulesForUser, singleUserRules } = useMemo<{
    rulesForUser: Record<number, { rule: Rule; userReq: UserRequirement }>;
    singleUserRules: Set<number>;
  }>(() => {
    const singleUserRules = new Set<number>();
    if (!selectedUserId) return { rulesForUser: {}, singleUserRules };

    const rulesForUser = Object.entries(allUserReqRules).reduce<
      Record<number, { rule: Rule; userReq: UserRequirement }>
    >((acc, [ruleId, ruleReqRule]) => {
      if (selectedUserId in ruleReqRule.user_reqs) {
        // if this is the only user in this rule, it's a single user rule
        if (Object.keys(ruleReqRule.user_reqs).length === 1) {
          singleUserRules.add(Number(ruleId));
        }
        const userReq = ruleReqRule.user_reqs[Number(selectedUserId)];
        acc[Number(ruleId)] = {
          rule: ruleReqRule.rule,
          userReq,
        };
      }
      return acc;
    }, {});

    return { rulesForUser, singleUserRules };
  }, [selectedUserId, allUserReqRules]);

  const [activeTab, setActiveTab] = useState<RuleTabType>(
    tab === RuleTabType.EXCLUSION
      ? RuleTabType.EXCLUSION
      : RuleTabType.INCLUSION,
  );

  const handleTabChange = (newTab: RuleTabType) => {
    setActiveTab(newTab);
  };

  // INCLUSION & EXCLUSION RULES THAT CAN BE PINNED
  const [clickedRuleIds, setClickedRuleIds] = useState<Set<number> | null>(
    null,
  );

  // Toggle visibility of excluded slots, blocked days, and vacation days in inclusion rules
  const [hideExcludedSlots, setHideExcludedSlots] = useState(false);
  const [hideBlockedSlots, setHideBlockedSlots] = useState(false);
  const [hideVacationSlots, setHideVacationSlots] = useState(false);

  useEffect(() => {
    setHideExcludedSlots(false);
    setHideBlockedSlots(false);
    setHideVacationSlots(false);
    setClickedRuleIds(null);
  }, [activeTab]);

  // This decides whether we show the min/max duration columns in the table
  const hasDurationRules = useMemo(
    () =>
      Object.values(rulesForUser).some(
        ({ userReq }) =>
          userReq.req_type === UserRequirementType.DURATION &&
          userReq.min_duration &&
          userReq.max_duration,
      ),
    [rulesForUser],
  );

  const hasSlotsRules = useMemo(
    () =>
      Object.values(rulesForUser).some(
        ({ userReq }) =>
          userReq.req_type === UserRequirementType.SLOTS &&
          userReq.min_slots &&
          userReq.max_slots,
      ),
    [rulesForUser],
  );

  const hasCounterUnitRules = useMemo(
    () =>
      Object.values(rulesForUser).some(
        ({ userReq }) =>
          userReq.req_type === UserRequirementType.COUNTER_UNIT &&
          userReq.min_counter_unit &&
          userReq.max_counter_unit,
      ),
    [rulesForUser],
  );

  const handleRuleClick = (ruleId: number) => {
    const params = new URLSearchParams();
    params.set('user', String(selectedUserId));
    params.set('tab', activeTab.toString());
    navigate(`${ruleId}?${params.toString()}`, { replace: false });
    window.scrollTo(0, 0);
  };

  const sectionSlotsToRules = useMemo(() => {
    const mapping: Record<number, number[]> = {};

    Object.entries(sectionSlotsDict).forEach(([slotId, slot]) => {
      mapping[Number(slotId)] = [];
    });

    Object.entries(rulesForUser).forEach(([ruleId, { rule }]) => {
      const sectionSlots = allUserReqRules[Number(ruleId)].section_slots;
      sectionSlots.forEach(slotId => {
        mapping[slotId].push(Number(ruleId));
      });
    });

    return mapping;
  }, [sectionSlotsDict, rulesForUser, allUserReqRules]);

  const hasUnregulatedSlots =
    Object.values(sectionSlotsToRules).filter(rules => rules.length === 0)
      .length > 0;

  // Separate rules into inclusion and exclusion
  const { inclusionRules, exclusionRules } = useMemo(() => {
    const inclusion: Array<{ rule: Rule; userReq: UserRequirement }> = [];
    const exclusion: Array<{ rule: Rule; userReq: UserRequirement }> = [];

    Object.entries(rulesForUser).forEach(([ruleId, { rule, userReq }]) => {
      const isExclusion = isExclusionRuleRequirement(userReq);

      if (isExclusion) {
        exclusion.push({ rule, userReq });
      } else {
        inclusion.push({ rule, userReq });
      }
    });

    return {
      inclusionRules: inclusion,
      exclusionRules: exclusion,
    };
  }, [rulesForUser]);

  const { singleUserInclusionRules, groupInclusionRules } = useMemo(() => {
    const singleUser: Array<{ rule: Rule; userReq: UserRequirement }> = [];
    const group: Array<{ rule: Rule; userReq: UserRequirement }> = [];

    inclusionRules.forEach(({ rule, userReq }) => {
      if (singleUserRules.has(rule.id_rule)) {
        singleUser.push({ rule, userReq });
      } else {
        group.push({ rule, userReq });
      }
    });

    return {
      singleUserInclusionRules: singleUser,
      groupInclusionRules: group,
    };
  }, [singleUserRules, inclusionRules]);

  const { singleUserExclusionRules, groupExclusionRules } = useMemo(() => {
    const singleUser: Array<{ rule: Rule; userReq: UserRequirement }> = [];
    const group: Array<{ rule: Rule; userReq: UserRequirement }> = [];

    exclusionRules.forEach(({ rule, userReq }) => {
      if (rule.type === 'CUSTOM_USER_REQS') {
        singleUser.push({ rule, userReq });
      } else {
        group.push({ rule, userReq });
      }
    });

    return {
      singleUserExclusionRules: singleUser,
      groupExclusionRules: group,
    };
  }, [exclusionRules]);

  const blocksForUserPerSlot = useMemo(() => {
    if (!selectedUserId) return {};

    return Object.entries(epa.preferencesMap.bySectionSlotbyUser).reduce<
      Record<number, UserPreference>
    >((acc, [slotId, userPreferences]) => {
      const preference = userPreferences[selectedUserId];
      if (
        preference !== undefined &&
        (preference.preference === UserPreferenceType.JUSTIFIED_BLOCKING ||
          preference.preference === UserPreferenceType.PERSONAL_BLOCKING)
      ) {
        acc[Number(slotId)] = preference;
      }
      return acc;
    }, {});
  }, [epa.preferencesMap, selectedUserId]);

  const eventDaysForUser = useMemo(() => {
    if (!selectedUserId) return {};
    return epa.eventsMap.byUserbyDate[selectedUserId];
  }, [epa.eventsMap, selectedUserId]);

  const eventsPerSlotForUser = useMemo(() => {
    if (!selectedUserId) return {};

    return Object.entries(sectionsWithSlots).reduce<
      Record<number, SpecialEventWithoutDates[]>
    >((acc, [_, section]) => {
      Object.entries(section.section_slots).forEach(([slotId, slot]) => {
        const slotStartDate = new Date(slot.start);
        const dateKey = dateToString(slotStartDate, 'iso');
        const eventsForDate = eventDaysForUser?.[dateKey] || [];

        if (eventsForDate.length > 0) {
          acc[Number(slotId)] = eventsForDate;
        }
      });
      return acc;
    }, {});
  }, [sectionsWithSlots, eventDaysForUser, selectedUserId]);

  const calculateRuleStats = useMemo(() => {
    const stats: Record<number, { total: number; available: number }> = {};

    Object.entries(rulesForUser).forEach(([ruleId, { rule, userReq }]) => {
      const sectionSlots = allUserReqRules[Number(ruleId)].section_slots;
      let total = 0;
      let available = 0;

      sectionSlots.forEach(slotId => {
        const slot = sectionSlotsDict[slotId];
        if (!slot) return;

        // Calculate total based on rule type
        switch (userReq.req_type) {
          case UserRequirementType.SLOTS:
            total += 1;
            break;
          case UserRequirementType.DURATION:
            const start = new Date(slot.start);
            const end = new Date(slot.end);
            const durationMinutes =
              (end.getTime() - start.getTime()) / (1000 * 60);
            total += durationMinutes;
            break;
          case UserRequirementType.COUNTER_UNIT:
            const idCounter = rule.id_custom_counter;
            total += idCounter
              ? (slot.custom_counter_increments[idCounter] ?? 0)
              : 0;
            break;
          default:
            break;
        }

        // Check if slot is available (not excluded, blocked, or vacation)
        const rulesForSlot = sectionSlotsToRules[slotId];
        const isExcluded = rulesForSlot.some(otherRuleId => {
          if (Number(otherRuleId) === Number(ruleId)) return false;
          const otherUserReq = rulesForUser[otherRuleId].userReq;
          return isExclusionRuleRequirement(otherUserReq);
        });

        const hasBlock = blocksForUserPerSlot?.[slotId] !== undefined;
        const hasVacation = eventsPerSlotForUser?.[slotId]?.length > 0;

        if (!isExcluded && !hasBlock && !hasVacation) {
          switch (userReq.req_type) {
            case UserRequirementType.SLOTS:
              available += 1;
              break;
            case UserRequirementType.DURATION:
              const start = new Date(slot.start);
              const end = new Date(slot.end);
              const durationMinutes =
                (end.getTime() - start.getTime()) / (1000 * 60);
              available += durationMinutes;
              break;
            case UserRequirementType.COUNTER_UNIT:
              const idCounter = rule.id_custom_counter;
              available += idCounter
                ? (slot.custom_counter_increments[idCounter] ?? 0)
                : 0;
              break;
            default:
              break;
          }
        }
      });

      // Convert duration to hours only at the end
      if (userReq.req_type === UserRequirementType.DURATION) {
        total /= 60;
        available /= 60;
      }

      stats[Number(ruleId)] = {
        total,
        available,
      };
    });

    return stats;
  }, [
    rulesForUser,
    allUserReqRules,
    sectionSlotsDict,
    blocksForUserPerSlot,
    eventsPerSlotForUser,
    sectionSlotsToRules,
  ]);

  const filteredSectionsWithSlots: SectionWithSlots[] = useMemo(
    () =>
      sectionsWithSlots.map(sectionWithSlots => {
        const filteredSectionSlots = Object.entries(
          sectionWithSlots.section_slots,
        ).reduce<Record<number, SectionSlot>>((acc, [slotId, slot]) => {
          const idSectionSlot = Number(slotId);
          const rulesOfSectionSlot = sectionSlotsToRules[idSectionSlot]
            ? sectionSlotsToRules[idSectionSlot]
            : [];

          const slotRequirements = rulesOfSectionSlot.map(
            ruleId => rulesForUser[ruleId],
          );

          const hasClickedRule =
            clickedRuleIds !== null &&
            clickedRuleIds !== undefined &&
            rulesOfSectionSlot.some(ruleId => clickedRuleIds?.has(ruleId));

          const blockOfSlot = blocksForUserPerSlot?.[idSectionSlot];
          const eventsOfSlot = eventsPerSlotForUser?.[idSectionSlot] || [];

          // If a rule is being clicked and this slot doesn't have that rule, hide it
          if (
            clickedRuleIds !== null &&
            clickedRuleIds !== undefined &&
            !hasClickedRule
          ) {
            return acc;
          }

          if (
            rulesOfSectionSlot.length === 0 &&
            activeTab === RuleTabType.UNINCLUSION
          ) {
            acc[idSectionSlot] = slot;
          }

          // (any rule has 0-0 requirements)
          const isExcluded = slotRequirements.some(req =>
            isExclusionRuleRequirement(req.userReq),
          );

          // in inclusion, we show all slots unless they're hidden
          if (activeTab === RuleTabType.INCLUSION) {
            if (isExcluded && hideExcludedSlots) {
              return acc;
            }
            if (blockOfSlot && hideBlockedSlots) {
              return acc;
            }
            if (eventsOfSlot.length > 0 && hideVacationSlots) {
              return acc;
            }
            acc[idSectionSlot] = slot;
          }

          // in exclusion, we only show excluded slots (from all or selected rules)
          if (activeTab === RuleTabType.EXCLUSION) {
            if (isExcluded) {
              acc[Number(slotId)] = slot;
            }
          }

          return acc;
        }, {});

        return {
          ...sectionWithSlots,
          section_slots: filteredSectionSlots,
        };
      }),
    [
      sectionsWithSlots,
      sectionSlotsToRules,
      clickedRuleIds,
      blocksForUserPerSlot,
      eventsPerSlotForUser,
      activeTab,
      rulesForUser,
      hideExcludedSlots,
      hideBlockedSlots,
      hideVacationSlots,
    ],
  );

  // TODO poder editar las reqs desde aqui!

  // que haya que darle a ver las prefs?

  // TODO: nueva regla de usuario desde aquí!

  return (
    <Wrapper>
      <div className="flex flex-col gap-4">
        <div className="flex flex-row items-center justify-center gap-2 mb-3">
          <button onClick={() => changeSelectedPerson('previous')}>
            <ArrowLeftIcon className="h-5 w-5 text-blue-600" />
          </button>
          <select
            id="user"
            name="user"
            className="block rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-blue-600 sm:text-sm sm:leading-6"
            value={selectedUserId || ''}
            onChange={handleUserChange}
          >
            {sortedUsersList.map(user => (
              <option key={user.id} value={user.id}>
                {`${user.firstname} ${user.lastname}`}
              </option>
            ))}
          </select>
          <button onClick={() => changeSelectedPerson('next')}>
            <ArrowRightIcon className="h-5 w-5 text-blue-600" />
          </button>
        </div>

        {/* Tab Buttons */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => handleTabChange(RuleTabType.INCLUSION)}
            className={`p-4 rounded-lg border-2 transition-all ${
              activeTab === RuleTabType.INCLUSION
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-200 hover:border-blue-200 hover:bg-blue-50/50'
            }`}
          >
            <h3 className="text-lg font-medium mb-2">
              {t('manager.rulesConfig.howMuchAndWhere')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('manager.rulesConfig.inclusionRulesDescription')}
            </p>
          </button>

          <button
            onClick={() => handleTabChange(RuleTabType.EXCLUSION)}
            className={`p-4 rounded-lg border-2 transition-all ${
              activeTab === RuleTabType.EXCLUSION
                ? 'border-red-500 bg-red-50 text-red-700'
                : 'border-gray-200 hover:border-red-200 hover:bg-red-50/50'
            }`}
          >
            <h3 className="text-lg font-medium mb-2">
              {t('manager.rulesConfig.whereCannotWork')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('manager.rulesConfig.exclusionRulesDescription')}
            </p>
          </button>
        </div>
        {hasUnregulatedSlots && (
          <button
            onClick={() => handleTabChange(RuleTabType.UNINCLUSION)}
            className={`rounded-lg p-1 border-2 transition-all flex flex-row items-center gap-2 justify-center ${
              activeTab === RuleTabType.UNINCLUSION
                ? 'border-yellow-500 bg-yellow-50 text-yellow-700'
                : 'border-yellow-200 hover:border-yellow-200 bg-yellow-50/50 hover:bg-yellow-50'
            }`}
          >
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-800" />
            <h3 className="text-md font-medium">
              {t('manager.rulesConfig.whereNotRegulated')}
            </h3>
          </button>
        )}

        {/* Content Sections */}
        <div className="overflow-x-auto">
          {activeTab === RuleTabType.INCLUSION && (
            <div className="flex-1">
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">
                    {t('manager.rulesConfig.inclusionRules')}
                  </h3>
                  <div className="mt-2 flex gap-4">
                    <button
                      type="button"
                      className={`px-3 py-1.5 rounded-md border flex items-center gap-2 transition-colors ${
                        hideExcludedSlots
                          ? 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                      onClick={() => setHideExcludedSlots(!hideExcludedSlots)}
                    >
                      {hideExcludedSlots ? (
                        <>
                          <EyeIcon className="h-5 w-5" />
                          {t('manager.rulesConfig.showExcludedSlots')}
                        </>
                      ) : (
                        <>
                          <NoSymbolIcon className="h-5 w-5" />
                          {t('manager.rulesConfig.hideExcludedSlots')}
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-1.5 rounded-md border flex items-center gap-2 transition-colors ${
                        hideBlockedSlots
                          ? 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                      onClick={() => setHideBlockedSlots(!hideBlockedSlots)}
                    >
                      {hideBlockedSlots ? (
                        <>
                          <EyeIcon className="h-5 w-5" />
                          {t('manager.rulesConfig.showBlockedSlots')}
                        </>
                      ) : (
                        <>
                          <NoSymbolIcon className="h-5 w-5" />
                          {t('manager.rulesConfig.hideBlockedSlots')}
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-1.5 rounded-md border flex items-center gap-2 transition-colors ${
                        hideVacationSlots
                          ? 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                      onClick={() => setHideVacationSlots(!hideVacationSlots)}
                    >
                      {hideVacationSlots ? (
                        <>
                          <EyeIcon className="h-5 w-5" />
                          {t('manager.rulesConfig.showVacationSlots')}
                        </>
                      ) : (
                        <>
                          <NoSymbolIcon className="h-5 w-5" />
                          {t('manager.rulesConfig.hideVacationSlots')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  {inclusionRules.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500 mb-4">
                        {t('manager.rulesConfig.noInclusionRules')}
                      </p>
                      <button
                        onClick={() =>
                          navigate(
                            `new?user=${selectedUserId}&inclusionType=INCLUSION`,
                          )
                        }
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        {t('manager.rulesConfig.createFirstInclusionRule')}
                      </button>
                    </div>
                  ) : (
                    <div>
                      <table className="bg-gray-50 min-w-full">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="px-4 py-2 text-left">
                              {t('generic.name')}
                            </th>
                            <th className="px-4 py-2 text-right">
                              {t('generic.min')}
                            </th>
                            <th className="px-4 py-2 text-right">
                              {t('generic.max')}
                            </th>
                            {/* <th className="px-4 py-2 text-right">
                              {hasSlotsRules &&
                              !hasDurationRules &&
                              !hasCounterUnitRules
                                ? t('manager.rulesConfig.totalSlots')
                                : t('generic.total')}
                            </th> */}
                            <th className="px-4 py-2 text-right">
                              {hasSlotsRules &&
                              !hasDurationRules &&
                              !hasCounterUnitRules
                                ? t('manager.rulesConfig.availableSlots')
                                : t('generic.available')}
                            </th>
                            <th className="px-4 py-2 text-right" />
                          </tr>
                        </thead>
                        <tbody>
                          {groupInclusionRules.length > 0 && (
                            <>
                              <tr className="bg-gray-50">
                                <td
                                  colSpan={6}
                                  className="px-4 py-2 font-medium text-blue-600 bg-blue-100"
                                >
                                  <div className="flex items-center gap-2">
                                    <UserGroupIcon className="h-5 w-5 text-blue-600" />
                                    {t('manager.rulesConfig.groupRules')}
                                  </div>
                                </td>
                              </tr>
                              {groupInclusionRules.map(({ rule, userReq }) => (
                                <tr
                                  key={rule.id_rule}
                                  className={`border-b border-gray-200 cursor-pointer rounded-lg ${
                                    clickedRuleIds?.has(rule.id_rule)
                                      ? 'bg-gray-100'
                                      : ''
                                  }`}
                                  onClick={() => {
                                    if (clickedRuleIds?.has(rule.id_rule)) {
                                      const newSet = new Set(clickedRuleIds);
                                      newSet.delete(rule.id_rule);
                                      setClickedRuleIds(
                                        newSet.size > 0 ? newSet : null,
                                      );
                                    } else {
                                      const newSet = new Set(
                                        clickedRuleIds || [],
                                      );
                                      newSet.add(rule.id_rule);
                                      setClickedRuleIds(newSet);
                                    }
                                  }}
                                >
                                  <td className="px-4 py-2 flex items-center gap-2">
                                    <PaperClipIcon
                                      className={`h-5 w-5 transition-opacity ${
                                        clickedRuleIds?.has(rule.id_rule)
                                          ? 'text-blue-600 hover:text-blue-800 hover:scale-110 transition-all'
                                          : 'text-gray-700 hover:text-gray-900 hover:scale-110 transition-all'
                                      }`}
                                    />
                                    {rule.name}
                                  </td>
                                  <td className="px-4 py-2 text-right">
                                    {formatValue(
                                      userReq.req_type,
                                      userReq.min_slots ||
                                        userReq.min_duration ||
                                        userReq.min_counter_unit ||
                                        0,
                                      t,
                                      minutesToHours,
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-right">
                                    {formatValue(
                                      userReq.req_type,
                                      userReq.max_slots ||
                                        userReq.max_duration ||
                                        userReq.max_counter_unit ||
                                        0,
                                      t,
                                      minutesToHours,
                                    )}
                                  </td>
                                  {/* <td className="px-4 py-2 text-right">
                                    {formatValue(
                                      userReq.req_type,
                                      calculateRuleStats[rule.id_rule].total,
                                      t,
                                      minutesToHours,
                                    )}
                                  </td> */}
                                  <td className="px-4 py-2 text-right">
                                    {formatValue(
                                      userReq.req_type,
                                      calculateRuleStats[rule.id_rule]
                                        .available,
                                      t,
                                      minutesToHours,
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-right">
                                    <button
                                      onClick={() =>
                                        handleRuleClick(rule.id_rule)
                                      }
                                      className="p-1 rounded-full border border-gray-300"
                                    >
                                      <Bars4Icon className="w-5 h-5 text-gray-400" />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </>
                          )}
                          {singleUserInclusionRules.length > 0 && (
                            <>
                              <tr className="bg-gray-50">
                                <td
                                  colSpan={6}
                                  className="px-4 py-2 font-medium text-blue-700 bg-blue-100"
                                >
                                  <div className="flex items-center gap-2">
                                    <UserIcon className="h-5 w-5 text-blue-500" />
                                    {t('manager.rulesConfig.singleUserRules')}
                                  </div>
                                </td>
                              </tr>
                              {singleUserInclusionRules.map(
                                ({ rule, userReq }) => (
                                  <tr
                                    key={rule.id_rule}
                                    className={`border-b border-gray-200 cursor-pointer rounded-lg ${
                                      clickedRuleIds?.has(rule.id_rule)
                                        ? 'bg-gray-100'
                                        : ''
                                    }`}
                                    onClick={() => {
                                      if (clickedRuleIds?.has(rule.id_rule)) {
                                        const newSet = new Set(clickedRuleIds);
                                        newSet.delete(rule.id_rule);
                                        setClickedRuleIds(
                                          newSet.size > 0 ? newSet : null,
                                        );
                                      } else {
                                        const newSet = new Set(
                                          clickedRuleIds || [],
                                        );
                                        newSet.add(rule.id_rule);
                                        setClickedRuleIds(newSet);
                                      }
                                    }}
                                  >
                                    <td className="px-4 py-2 flex items-center gap-2">
                                      <PaperClipIcon
                                        className={`h-5 w-5 transition-opacity ${
                                          clickedRuleIds?.has(rule.id_rule)
                                            ? 'text-blue-600 hover:text-blue-800 hover:scale-110 transition-all'
                                            : 'text-gray-700 hover:text-gray-900  hover:scale-110 transition-all'
                                        }`}
                                      />
                                      {rule.name}
                                    </td>
                                    <td className="px-4 py-2 text-right">
                                      {formatValue(
                                        userReq.req_type,
                                        userReq.min_slots ||
                                          userReq.min_duration ||
                                          userReq.min_counter_unit ||
                                          0,
                                        t,
                                        minutesToHours,
                                      )}
                                    </td>
                                    <td className="px-4 py-2 text-right">
                                      {formatValue(
                                        userReq.req_type,
                                        userReq.max_slots ||
                                          userReq.max_duration ||
                                          userReq.max_counter_unit ||
                                          0,
                                        t,
                                        minutesToHours,
                                      )}
                                    </td>
                                    <td className="px-4 py-2 text-right">
                                      {formatValue(
                                        userReq.req_type,
                                        calculateRuleStats[rule.id_rule].total,
                                        t,
                                        minutesToHours,
                                      )}
                                    </td>
                                    <td className="px-4 py-2 text-right">
                                      {formatValue(
                                        userReq.req_type,
                                        calculateRuleStats[rule.id_rule]
                                          .available,
                                        t,
                                        minutesToHours,
                                      )}
                                    </td>
                                    <td className="px-4 py-2 text-right">
                                      <button
                                        onClick={() =>
                                          handleRuleClick(rule.id_rule)
                                        }
                                        className="p-1 rounded-full border border-gray-300"
                                      >
                                        <Bars4Icon className="w-5 h-5 text-gray-400" />
                                      </button>
                                    </td>
                                  </tr>
                                ),
                              )}
                            </>
                          )}
                          <tr>
                            <td colSpan={6} className="px-4 py-4">
                              <button
                                onClick={() =>
                                  navigate(
                                    `new?user=${selectedUserId}&inclusionType=INCLUSION`,
                                  )
                                }
                                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                {t('manager.rulesConfig.createInclusionRule', {
                                  name: `${users[Number(selectedUserId)].firstname} ${users[Number(selectedUserId)].lastname}`,
                                })}
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === RuleTabType.EXCLUSION && (
            <div className="flex-1">
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="p-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">
                    {t('manager.rulesConfig.exclusionRules')}
                  </h3>
                </div>
                <div className="p-4">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                          {t('generic.name')}
                        </th>
                        <th className="px-4 py-2 text-right text-sm font-medium text-gray-700" />
                      </tr>
                    </thead>
                    <tbody>
                      {groupExclusionRules.length > 0 && (
                        <>
                          <tr className="bg-gray-50">
                            <td
                              colSpan={2}
                              className="px-4 py-2 font-medium text-red-700 bg-red-100"
                            >
                              <div className="flex items-center gap-2">
                                <UserGroupIcon className="h-5 w-5 text-red-500" />
                                {t('manager.rulesConfig.groupRules')}
                              </div>
                            </td>
                          </tr>
                          {groupExclusionRules.map(({ rule }) => (
                            <tr
                              key={rule.id_rule}
                              className={`border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors ${
                                clickedRuleIds?.has(rule.id_rule)
                                  ? 'bg-gray-50'
                                  : ''
                              }`}
                              onClick={() => {
                                if (clickedRuleIds?.has(rule.id_rule)) {
                                  const newSet = new Set(clickedRuleIds);
                                  newSet.delete(rule.id_rule);
                                  setClickedRuleIds(
                                    newSet.size > 0 ? newSet : null,
                                  );
                                } else {
                                  const newSet = new Set(clickedRuleIds || []);
                                  newSet.add(rule.id_rule);
                                  setClickedRuleIds(newSet);
                                }
                              }}
                            >
                              <td className="px-4 py-2 flex items-center gap-2">
                                <PaperClipIcon
                                  className={`h-5 w-5 transition-opacity ${
                                    clickedRuleIds?.has(rule.id_rule)
                                      ? 'text-blue-600 hover:text-blue-800 hover:scale-110 transition-all'
                                      : 'text-gray-700 hover:text-gray-900  hover:scale-110 transition-all'
                                  }`}
                                />
                                <span className="text-gray-900">
                                  {rule.name}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-right">
                                <button
                                  onClick={() => handleRuleClick(rule.id_rule)}
                                  className="p-1 rounded-full border border-gray-200 hover:bg-gray-100 transition-colors"
                                >
                                  <Bars4Icon className="w-5 h-5 text-gray-400" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </>
                      )}
                      <tr className="bg-gray-50">
                        <td
                          colSpan={2}
                          className="px-4 py-2 font-medium text-blredue-700 bg-red-100"
                        >
                          <div className="flex items-center gap-2">
                            <UserIcon className="h-5 w-5 text-red-500" />
                            {t('manager.rulesConfig.singleUserRules')}
                          </div>
                        </td>
                      </tr>
                      {singleUserExclusionRules.length > 0 ? (
                        singleUserExclusionRules.map(({ rule }) => (
                          <tr
                            key={rule.id_rule}
                            className={`border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors ${
                              clickedRuleIds?.has(rule.id_rule)
                                ? 'bg-gray-50'
                                : ''
                            }`}
                            onClick={() => {
                              if (clickedRuleIds?.has(rule.id_rule)) {
                                const newSet = new Set(clickedRuleIds);
                                newSet.delete(rule.id_rule);
                                setClickedRuleIds(
                                  newSet.size > 0 ? newSet : null,
                                );
                              } else {
                                const newSet = new Set(clickedRuleIds || []);
                                newSet.add(rule.id_rule);
                                setClickedRuleIds(newSet);
                              }
                            }}
                          >
                            <td className="px-4 py-2 flex items-center gap-2">
                              <PaperClipIcon
                                className={`h-5 w-5 transition-opacity ${
                                  clickedRuleIds?.has(rule.id_rule)
                                    ? 'text-blue-600 hover:text-blue-800 hover:scale-110 transition-all'
                                    : 'text-gray-700 hover:text-gray-900  hover:scale-110 transition-all'
                                }`}
                              />
                              <span className="text-gray-900">{rule.name}</span>
                            </td>
                            <td className="px-4 py-2 text-right">
                              <button
                                onClick={() => handleRuleClick(rule.id_rule)}
                                className="p-1 rounded-full border border-gray-200 hover:bg-gray-100 transition-colors"
                              >
                                <Bars4Icon className="w-5 h-5 text-gray-400" />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className="border-b border-gray-200">
                          <td colSpan={2} className="px-4 py-4">
                            <button
                              onClick={() =>
                                navigate(
                                  `new?user=${selectedUserId}&inclusionType=EXCLUSION`,
                                )
                              }
                              className="w-full inline-flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 text-sm font-medium rounded-md text-gray-700 hover:border-gray-400 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                            >
                              {t(
                                'manager.rulesConfig.createExclusionRuleForUser',
                              )}
                            </button>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === RuleTabType.UNINCLUSION && (
            <div className="flex-1">
              <h3 className="text-lg font-medium mb-2">
                {t('manager.rulesConfig.unregulatedRules')}
              </h3>
              <div className="text-gray-500">
                {t('manager.rulesConfig.unregulatedRulesExplanation')}
              </div>
            </div>
          )}
        </div>

        <div className="border rounded-lg p-4">
          {/* <ScheduleGridRulesForUser
            start={iteration.start_day}
            end={iteration.end_day}
            sectionsWithSlots={filteredSectionsWithSlots}
            activeRulesForUserTab={activeTab}
            sectionSlotsToRules={sectionSlotsToRules}
            rulesForUser={rulesForUser}
            blocksForUserPerSlot={blocksForUserPerSlot}
            eventsPerSlotForUser={eventsPerSlotForUser}
          /> */}
          <Virgueria
            start={iteration.start_day}
            end={iteration.end_day}
            sectionsWithSlots={filteredSectionsWithSlots}
            activeRulesForUserTab={activeTab}
            labels={Object.values(slotLabels)}
            version={VirgueriaVersion.RulesForUser}
            sectionSlotsToRules={sectionSlotsToRules}
            rulesForUser={rulesForUser}
            blocksForUserPerSlot={blocksForUserPerSlot}
          />
        </div>
      </div>
    </Wrapper>
  );
}
